import React, { useState } from 'react';
import { Typography, Select } from 'antd';
import { message } from '../../../services/toastMessage';
import { useTranslation } from 'react-i18next';
import { useUserProfile } from '../../../contextProviders/UserProfileProvider';
import { updateMyUserProfile } from '../../../services/UserProfileService';
import { TIMEZONE_OPTIONS } from '../../../constants/heavyDutyMachinerySpaceOptions';
import SettingsCard from '../../../components/Settings/SettingsCard';
import SettingsRowShell from '../../../components/Settings/SettingsRowShell';
import { useAppBreakpoint } from '../../../hooks/useAppBreakpoint';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';
import { APP_LANGUAGE_OPTIONS } from '../../../constants/appLanguages';

const { Title, Text } = Typography;

/** "Configuración > Idioma y zona horaria" -- the display-currency preference lives in its own sibling section, CurrencySection.tsx, not here. It's display-only: a guest's actual charge currency always follows the host's own at booking time (see Booking.GuestCurrencyCode/currencyHelper's own doc comments), never this preference. */
const LanguageSection: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const { userProfile, refresh } = useUserProfile();
  const [savingField, setSavingField] = useState<'language' | 'timezone' | null>(null);

  const handleLanguageChange = async (lang: string) => {
    setSavingField('language');
    try {
      await updateMyUserProfile({ preferredLanguage: lang });
      await refresh();
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.PROFILE_UPDATED_MESSAGE));
    } finally {
      setSavingField(null);
    }
  };

  const handleTimezoneChange = async (timezone: string) => {
    setSavingField('timezone');
    try {
      await updateMyUserProfile({ preferredTimezone: timezone });
      await refresh();
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.PROFILE_UPDATED_MESSAGE));
    } finally {
      setSavingField(null);
    }
  };

  return (
    <div>
      {/* Redundant on mobile -- the list->detail header (Settings.tsx) already shows this section's name. */}
      {!isMobile && (
        <Title level={4} style={{ marginBottom: 20 }}>
          {translate(t, TRANSLATIONS.SETTINGS_NAV_LANGUAGE)}
        </Title>
      )}

      <SettingsCard noMarginBottom>
        <SettingsRowShell
          left={<Text strong>{translate(t, TRANSLATIONS.MY_ACCOUNT_LANGUAGE_LABEL)}</Text>}
          right={
            <Select
              style={{ width: 200 }}
              value={userProfile?.preferredLanguage}
              loading={savingField === 'language'}
              onChange={handleLanguageChange}
              options={APP_LANGUAGE_OPTIONS}
            />
          }
        />
        <SettingsRowShell
          left={<Text strong>{translate(t, TRANSLATIONS.SETTINGS_TIMEZONE_LABEL)}</Text>}
          right={
            <Select
              style={{ width: 200 }}
              showSearch
              value={userProfile?.preferredTimezone}
              loading={savingField === 'timezone'}
              onChange={handleTimezoneChange}
              filterOption={(input, option: any) => option?.label?.toLowerCase().includes(input.toLowerCase())}
              options={TIMEZONE_OPTIONS.map((o) => ({ value: o.value, label: translate(t, o.labelKey) }))}
            />
          }
        />
      </SettingsCard>
    </div>
  );
};

export default LanguageSection;
